import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  GlobalSignOutCommand,
} from '@aws-sdk/client-cognito-identity-provider';

const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const REGION = 'us-east-1';

const cognitoClient = new CognitoIdentityProviderClient({ region: REGION });

interface StoredTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

const TOKEN_KEY = 'mojofinds_admin_tokens';

function getStoredTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeTokens(tokens: StoredTokens) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  idToken: string | null;
  getValidToken: () => Promise<string>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [tokens, setTokens] = useState<StoredTokens | null>(null);

  // On mount, check for stored tokens and refresh if needed
  useEffect(() => {
    const init = async () => {
      const stored = getStoredTokens();
      if (!stored) {
        setIsLoading(false);
        return;
      }

      // If token expires within 5 minutes, refresh
      if (stored.expiresAt - Date.now() < 5 * 60 * 1000) {
        try {
          const refreshed = await refreshTokens(stored.refreshToken);
          setTokens(refreshed);
        } catch {
          clearTokens();
        }
      } else {
        setTokens(stored);
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const command = new InitiateAuthCommand({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    });

    const response = await cognitoClient.send(command);

    if (!response.AuthenticationResult) {
      throw new Error('Authentication failed');
    }

    const newTokens: StoredTokens = {
      accessToken: response.AuthenticationResult.AccessToken!,
      idToken: response.AuthenticationResult.IdToken!,
      refreshToken: response.AuthenticationResult.RefreshToken!,
      expiresAt: Date.now() + (response.AuthenticationResult.ExpiresIn || 3600) * 1000,
    };

    storeTokens(newTokens);
    setTokens(newTokens);
  }, []);

  // Returns a valid (non-expired) ID token, refreshing if needed
  const getValidToken = useCallback(async (): Promise<string> => {
    const stored = getStoredTokens();
    if (!stored) throw new Error('Not authenticated');

    // If token expires within 5 minutes, refresh
    if (stored.expiresAt - Date.now() < 5 * 60 * 1000) {
      const refreshed = await refreshTokens(stored.refreshToken);
      setTokens(refreshed);
      return refreshed.idToken;
    }
    return stored.idToken;
  }, []);

  const logout = useCallback(async () => {
    if (tokens?.accessToken) {
      try {
        await cognitoClient.send(new GlobalSignOutCommand({ AccessToken: tokens.accessToken }));
      } catch {
        // Continue with local clear
      }
    }
    clearTokens();
    setTokens(null);
  }, [tokens]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!tokens && tokens.expiresAt > Date.now(),
        isLoading,
        idToken: tokens?.idToken ?? null,
        getValidToken,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

async function refreshTokens(refreshToken: string): Promise<StoredTokens> {
  const command = new InitiateAuthCommand({
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken,
    },
  });

  const response = await cognitoClient.send(command);

  if (!response.AuthenticationResult) {
    throw new Error('Token refresh failed');
  }

  const newTokens: StoredTokens = {
    accessToken: response.AuthenticationResult.AccessToken!,
    idToken: response.AuthenticationResult.IdToken!,
    refreshToken: refreshToken,
    expiresAt: Date.now() + (response.AuthenticationResult.ExpiresIn || 3600) * 1000,
  };

  storeTokens(newTokens);
  return newTokens;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
